import {action, makeObservable, observable} from 'mobx';
import _ from 'lodash';
import semver from 'semver';

import Config from 'config/Config';
import Value from 'helpers/Value';
import {StoryReleaseProvider} from 'models/storyRelease/StoryReleaseProvider';
import {AssetBundleManifest} from 'models/storyRelease/AssetBundleManifest';
import {
    IStoryReleaseApiDataCreateRequest,
    IStoryReleaseApiDataGetResponse
} from 'models/storyRelease/IStoryReleaseApiData';

export class StoryRelease {
    private readonly provider: StoryReleaseProvider;

    @observable public id: number;

    @observable public story_id: number;
    @observable public platform: string;
    @observable public major_version: number;
    @observable public minor_version: number;
    @observable public patch_version: number;
    @observable public sdk_major_version: number;
    @observable public sdk_minor_version: number;
    @observable public sdk_patch_version: number;
    @observable public compatible_versions: string[];
    @observable public current_chapter_bundle: string;
    @observable public is_current_release_for_story_id: number;
    @observable public unity_asset_url: string;
    @observable public unity_asset_manifest: AssetBundleManifest = new AssetBundleManifest();

    @observable public published_at: Date;
    @observable public created_at: Date;

    constructor(provider: StoryReleaseProvider) {
        makeObservable(this);

        this.provider = provider;

        this.withId = this.withId.bind(this);
        this.withData = this.withData.bind(this);
    }

    public withId(id: number): StoryRelease {
        this.id = id;
        return this;
    }

    public withData(data: IStoryReleaseApiDataGetResponse | StoryRelease): StoryRelease {
        this.load(data);
        return this;
    }

    @action.bound
    public load(data: IStoryReleaseApiDataGetResponse | StoryRelease) {
        this.id = data.id;
        this.story_id = data.story_id;

        this.platform = data.platform;
        this.major_version = data.major_version;
        this.minor_version = data.minor_version;
        this.patch_version = data.patch_version;
        this.sdk_major_version = data.sdk_major_version;
        this.sdk_minor_version = data.sdk_minor_version;
        this.sdk_patch_version = data.sdk_patch_version;
        this.compatible_versions = data.compatible_versions;

        this.unity_asset_url = data.unity_asset_url;
        this.unity_asset_manifest = new AssetBundleManifest().withData(data.unity_asset_manifest);

        this.published_at = Value.dateOrNull(data.published_at);
        this.created_at = Value.dateOrNull(data.created_at);
    }

    public published(): boolean {
        return this.published_at != null;
    }

    public version(): string {
        return `${this.major_version}.${this.minor_version}.${this.patch_version}`;
    }

    public sdkVersion(): string {
        return `${this.sdk_major_version}.${this.sdk_minor_version}.${this.sdk_patch_version}`;
    }

    public create(): Promise<any> {
        let storyReleaseApiData: IStoryReleaseApiDataCreateRequest = {
            story_id: this.story_id,
            platform: this.platform,
            major_version: this.major_version,
            minor_version: this.minor_version,
            patch_version: this.patch_version,
            sdk_major_version: this.sdk_major_version,
            sdk_minor_version: this.sdk_minor_version,
            sdk_patch_version: this.sdk_patch_version,
            compatible_versions: this.compatible_versions,
            unity_asset_url: this.unity_asset_url,
            unity_asset_manifest: this.unity_asset_manifest
        }

        return this.provider.create(storyReleaseApiData);
    }

    public setSDKVersion(sdk_major_version: number, sdk_minor_version: number, sdk_patch_version: number): void {
        this.sdk_major_version = sdk_major_version;
        this.sdk_minor_version = sdk_minor_version;
        this.sdk_patch_version = sdk_patch_version;
    }

    public isCompatibleWithSDK(): boolean {
        if (this.platform !== 'webgl') return false;
        const is_compatible: boolean = this.compatible_versions.some(version => semver.satisfies(Config.sdkVersion, StoryRelease.convertToSemVersion(version)));
        console.log (`(Release ${this.version()}) Comparing SDK [${this.compatible_versions.join(",")}] against '${Config.sdkVersion}' => ${is_compatible}`)
        return is_compatible;
    }

    /**
     * Converts custom version shorthand into a proper semver range:
     *   "3+"      -> ">=3.0.0"
     *   "2.1+"    -> ">=2.1.0 <3.0.0"
     *   "2.0.6+"  -> ">=2.0.6 <2.1.0"
     */
    public static convertToSemVersion(customRange: string): string {
      if (typeof customRange !== 'string' || !customRange.trim().endsWith('+')) {
        return customRange;
      }

      const base = customRange.trim().slice(0, -1); // remove '+'
      if (!/^\d+(?:\.\d+){0,2}$/.test(base)) {
        return customRange;
      }

      const parts = base.split('.').map(Number);
      // normalize to [major, minor, patch]
      const major = parts[0] ?? 0;
      const minor = parts[1] ?? 0;
      const patch = parts[2] ?? 0;

      // Determine upper bound
      let upperBound = '';
      if (parts.length === 1) {
        // e.g. 3+ → >=3.0.0
        upperBound = '';
      } else if (parts.length === 2) {
        // e.g. 2.1+ → >=2.1.0 <3.0.0
        upperBound = ` <${major + 1}.0.0`;
      } else {
        // e.g. 2.0.6+ → >=2.0.6 <2.1.0
        upperBound = ` <${major}.${minor + 1}.0`;
      }
      return `>=${major}.${minor}.${patch}${upperBound}`;
    }

    public hasAssetUrl(): boolean {
        let bundles = this.unity_asset_manifest?.asset_bundles;
        let bundlesWithUrl = _.filter(bundles, bundle => !!bundle.url);
        return this.unity_asset_url !== null || (bundles.length > 0 && bundles.length === bundlesWithUrl.length);
    }

    public static filterForLatest(releases: StoryRelease[]): StoryRelease | null {
        let res = releases.filter(release => release.isCompatibleWithSDK())
            .sort((release1: StoryRelease, release2: StoryRelease) => {
                let major1 = release1.major_version;
                let major2 = release2.major_version;
                let minor1 = release1.minor_version;
                let minor2 = release2.minor_version;
                let patch1 = release1.patch_version;
                let patch2 = release2.patch_version;
                if (isNaN(major1)) return -1;
                if (isNaN(major2)) return 1;
                if (major1 != major2) return major1 - major2;
                if (isNaN(minor1)) return -1;
                if (isNaN(minor2)) return 1;
                if (minor1 != minor2) return minor1 - minor2;
                if (isNaN(patch1)) return -1;
                if (isNaN(patch2)) return 1;
                return patch1 - patch2;
            });
        return res.length > 0 ? res[res.length - 1] : null;
    }

    public toJSON() {
        return {
            id: this.id,
            storyId: this.story_id,
            platform: this.platform == 'webgl' ? 2 : this.platform == 'ios' ? 1 : 0,
            majorVersion: this.major_version,
            minorVersion: this.minor_version,
            patchVersion: this.patch_version,
            sdkMajorVersion: this.sdk_major_version,
            sdkMinorVersion: this.sdk_minor_version,
            sdkPatchVersion: this.sdk_patch_version,
            compatibleVersions: this.compatible_versions,
            unityAssetUrl: this.unity_asset_url,
            publishedAt: this.published_at
        }
    }
}
